import {
	EventDispatcher,
	MOUSE,
	Quaternion,
	Spherical,
	TOUCH,
	Vector2,
	Vector3
} from 'three/build/three.module.js';

// This set of controls performs orbiting, moving forward and backwards, and panning.
// Unlike TrackballControls, it maintains the "up" direction object.up (+Y by default).
//
//    Orbit - left mouse / touch: one-finger move
//    Move - middle mouse, or mousewheel / touch: two-finger spread or squish
//    Pan - right mouse, or left mouse + ctrl/meta/shiftKey, or arrow keys / touch: two-finger move

var ItinerarisControls = function (object, domElement) {

	if (domElement === undefined) console.warn('THREE.ItinerarisControls: The second parameter "domElement" is now mandatory.');
	if (domElement === document) console.error('THREE.ItinerarisControls: "document" should not be used as the target "domElement". Please use "renderer.domElement" instead.');

	this.object = object;
	this.domElement = domElement;

	// Set to false to disable this control
	this.enabled = true;

	// How far you can orbit vertically, upper and lower limits.
	// Range is 0 to Math.PI radians.
	this.minPolarAngle = -Math.PI / 4; // radians
	this.maxPolarAngle = Math.PI / 4; // radians

	// Set to true to enable damping (inertia)
	// If damping is enabled, you must call controls.update() in your animation loop
	this.enableDamping = false;
	this.dampingFactor = 0.05;

	// This option actually enables moving forward and backwards
	// Set to false to disable moving
	this.enableMove = true;
	this.moveSpeed = 1.0;

	// Set to false to disable rotating
	this.enableRotate = true;
	this.rotateSpeed = 1.0;
	this.keyRotateSpeed = 0.25;

	// Set to false to disable panning
	this.enablePan = true;
	this.panSpeed = 1.0;
	this.keyPanSpeed = 2.0;	// pixels moved per arrow key push

	// The four arrow keys
	this.keys = {LEFT: 37, UP: 38, RIGHT: 39, BOTTOM: 40};
	this.keys_status = {37: false, 38: false, 39: false, 40: false};  // TODO: fer-ho dinàmic

	// Mouse buttons
	this.mouseButtons = {LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.PAN};

	// Touch fingers
	this.touches = {ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN};

	// for reset
	this.rotation0 = this.object.rotation.clone();
	this.position0 = this.object.position.clone();

	// the target DOM element for key events
	this._domElementKeyEvents = null;

	//
	// public methods
	//

	this.getPolarAngle = function () {

		return spherical.phi;

	};

	this.getAzimuthalAngle = function () {

		return spherical.theta;

	};

	this.listenToKeyEvents = function (domElement) {

		domElement.addEventListener('keydown', onKeyDown);
		domElement.addEventListener('keyup', onKeyUp);
		this._domElementKeyEvents = domElement;

	};

	this.saveState = function () {

		scope.rotation0.copy(scope.object.rotation);
		scope.position0.copy(scope.object.position);

	};

	this.reset = function () {

		scope.object.rotation.copy(scope.rotation0);
		scope.object.position.copy(scope.position0);

		scope.object.updateProjectionMatrix();
		scope.dispatchEvent(changeEvent);

		scope.update();

		state = STATE.NONE;

	};

	// this method is exposed, but perhaps it would be better if we can make it private...
	this.update = function () {

		var offset = new Vector3();

		// so camera.up is the orbit axis
		var quat = new Quaternion().setFromUnitVectors(object.up, new Vector3(0, 1, 0));
		var quatInverse = quat.clone().invert();

		var lastPosition = new Vector3();
		var lastQuaternion = new Quaternion();

		var twoPI = 2 * Math.PI;

		return function update() {

			if (scope.keys_status[scope.keys.UP]) {
				pan(0, -scope.keyPanSpeed * 2);
			}

			if (scope.keys_status[scope.keys.BOTTOM]) {
				pan(0, scope.keyPanSpeed * 2);
			}
			pan(0, -0.20);

			if (scope.keys_status[scope.keys.LEFT]) {
				rotateLeft(scope.keyRotateSpeed * -Math.PI / 50);
			}

			if (scope.keys_status[scope.keys.RIGHT]) {
				rotateLeft(scope.keyRotateSpeed * Math.PI / 50);
			}

			scope.object.rotation.order = 'YXZ';
			if (scope.enableDamping) {

				scope.object.rotation.x += sphericalDelta.phi * scope.dampingFactor;
				scope.object.rotation.y += sphericalDelta.theta * scope.dampingFactor;

			} else {

				scope.object.rotation.x += sphericalDelta.phi * scope.dampingFactor;
				scope.object.rotation.y += sphericalDelta.theta;

			}
			scope.object.rotation.x = Math.max(scope.minPolarAngle, Math.min(scope.maxPolarAngle, scope.object.rotation.x));

			scope.object.position.add(panOffset.add(new Vector3(0,0,0)));

			if (scope.enableDamping === true) {

				sphericalDelta.theta *= (1 - scope.dampingFactor);
				sphericalDelta.phi *= (1 - scope.dampingFactor);

				panOffset.multiplyScalar(1 - scope.dampingFactor);

			} else {

				sphericalDelta.set(0, 0, 0);

				panOffset.set(0, 0, 0);

			}

			scale = 1;

			// update condition is:
			// min(camera displacement, camera rotation in radians)^2 > EPS
			// using small-angle approximation cos(x/2) = 1 - x^2 / 8

			if (zoomChanged ||
				lastPosition.distanceToSquared(scope.object.position) > EPS ||
				8 * (1 - lastQuaternion.dot(scope.object.quaternion)) > EPS) {

				scope.dispatchEvent(changeEvent);

				lastPosition.copy(scope.object.position);
				lastQuaternion.copy(scope.object.quaternion);
				zoomChanged = false;

				return true;

			}

			return false;

		};

	}();

	this.dispose = function () {

		scope.domElement.removeEventListener('contextmenu', onContextMenu);

		scope.domElement.removeEventListener('pointerdown', onPointerDown);
		scope.domElement.removeEventListener('wheel', onMouseWheel);

		scope.domElement.removeEventListener('touchstart', onTouchStart);
		scope.domElement.removeEventListener('touchend', onTouchEnd);
		scope.domElement.removeEventListener('touchmove', onTouchMove);

		scope.domElement.ownerDocument.removeEventListener('pointermove', onPointerMove);
		scope.domElement.ownerDocument.removeEventListener('pointerup', onPointerUp);


		if (scope._domElementKeyEvents !== null) {

			scope._domElementKeyEvents.removeEventListener('keydown', onKeyDown);

		}

		//scope.dispatchEvent( { type: 'dispose' } ); // should this be added here?

	};

	//
	// internals
	//

	var scope = this;

	var changeEvent = {type: 'change'};
	var startEvent = {type: 'start'};
	var endEvent = {type: 'end'};

	var STATE = {
		NONE: -1,
		ROTATE: 0,
		MOVE: 1,
		PAN: 2,
		TOUCH_ROTATE: 3,
		TOUCH_PAN: 4,
		TOUCH_MOVE_PAN: 5,
		TOUCH_MOVE_ROTATE: 6
	};

	var state = STATE.NONE;

	var EPS = 0.000001;

	// current position in spherical coordinates
	var spherical = new Spherical();
	var sphericalDelta = new Spherical();

	var scale = 1;
	var panOffset = new Vector3();
	var zoomChanged = false;

	var rotateStart = new Vector2();
	var rotateEnd = new Vector2();
	var rotateDelta = new Vector2();

	var panStart = new Vector2();
	var panEnd = new Vector2();
	var panDelta = new Vector2();

	var moveStart = new Vector2();
	var moveEnd = new Vector2();
	var moveDelta = new Vector2();

	function getMoveScale() {

		return scope.moveSpeed;

	}

	function rotateLeft(angle) {

		sphericalDelta.theta -= angle;

	}

	function rotateUp(angle) {

		sphericalDelta.phi -= angle;

	}

	var panLeft = function () {

		var v = new Vector3();

		return function panLeft(distance, objectMatrix) {

			v.setFromMatrixColumn(objectMatrix, 0); // get X column of objectMatrix
			v.multiplyScalar(-distance);

			panOffset.add(v);

		};

	}();

	var panIn = function () {

		var v = new Vector3();

		return function panIn(distance, objectMatrix) {


			scope.object.getWorldDirection(v);
			v.y = 0;

			v.multiplyScalar(-distance);

			panOffset.add(v);

		};

	}();

	// deltaX and deltaY are in pixels; right and down are positive
	var pan = function () {

		return function pan(deltaX, deltaY) {

			var element = scope.domElement;

			const targetDistance = 0.25;

			// we use only clientHeight here so aspect ratio does not distort speed
			panLeft(2 * deltaX * targetDistance / element.clientHeight, scope.object.matrix);
			panIn(2 * deltaY * targetDistance / element.clientHeight, scope.object.matrix);


			// orthographic
			// panLeft( deltaX * ( scope.object.right - scope.object.left ) / scope.object.zoom / element.clientWidth, scope.object.matrix );
			// panUp( deltaY * ( scope.object.top - scope.object.bottom ) / scope.object.zoom / element.clientHeight, scope.object.matrix );

		};

	}();

	function moveOut(moveScale) {

		const direction = new Vector3();
		scope.object.getWorldDirection(direction);
		direction.y = 0;
		scope.object.position.add(direction.multiplyScalar(-0.1 * moveScale));

	}

	function moveIn(moveScale) {

		const direction = new Vector3();
		scope.object.getWorldDirection(direction);
		direction.y = 0;
		scope.object.position.add(direction.multiplyScalar(0.1 * moveScale));

	}

	//
	// event callbacks - update the object state
	//

	function handleMouseDownRotate(event) {

		rotateStart.set(event.clientX, event.clientY);

	}

	function handleMouseDownMove(event) {

		moveStart.set(event.clientX, event.clientY);

	}

	function handleMouseDownPan(event) {

		panStart.set(event.clientX, event.clientY);

	}

	function handleMouseMoveRotate(event) {

		rotateEnd.set(event.clientX, event.clientY);

		rotateDelta.subVectors(rotateEnd, rotateStart).multiplyScalar(scope.rotateSpeed);

		var element = scope.domElement;

		rotateLeft(2 * Math.PI * rotateDelta.x / element.clientHeight); // yes, height

		rotateUp(2 * Math.PI * rotateDelta.y / element.clientHeight);

		rotateStart.copy(rotateEnd);

		scope.update();

	}

	function handleMouseMoveMove(event) {

		moveEnd.set(event.clientX, event.clientY);

		moveDelta.subVectors(moveEnd, moveStart);

		if (moveDelta.y > 0) {

			moveOut(getMoveScale());

		} else if (moveDelta.y < 0) {

			moveIn(getMoveScale());

		}

		moveStart.copy(moveEnd);

		scope.update();

	}

	function handleMouseMovePan(event) {

		panEnd.set(event.clientX, event.clientY);

		panDelta.subVectors(panEnd, panStart).multiplyScalar(scope.panSpeed);

		pan(panDelta.x, panDelta.y);

		panStart.copy(panEnd);

		scope.update();

	}

	function handleMouseUp( /*event*/) {

		// no-op

	}

	function handleMouseWheel(event) {

		if (event.deltaY < 0) {

			moveIn(getMoveScale());

		} else if (event.deltaY > 0) {

			moveOut(getMoveScale());

		}

		scope.update();

	}

	function handleKeyDown(event) {

		var needsUpdate = false;

		switch (event.keyCode) {

			case scope.keys.UP:
			case scope.keys.BOTTOM:
			case scope.keys.LEFT:
			case scope.keys.RIGHT:
				scope.keys_status[event.keyCode] = true;
				needsUpdate = true;
				// console.log(scope.keys_status);
		}

		if (needsUpdate) {

			// prevent the browser from scrolling on cursor keys
			event.preventDefault();

			scope.update();

		}


	}

	function handleKeyUp(event) {

		scope.keys_status[event.keyCode] = false;
		// console.log(scope.keys_status);

	}

	function handleTouchStartRotate(event) {

		if (event.touches.length == 1) {

			rotateStart.set(event.touches[0].pageX, event.touches[0].pageY);

		} else {

			var x = 0.5 * (event.touches[0].pageX + event.touches[1].pageX);
			var y = 0.5 * (event.touches[0].pageY + event.touches[1].pageY);

			rotateStart.set(x, y);

		}

	}

	function handleTouchStartPan(event) {

		if (event.touches.length == 1) {

			panStart.set(event.touches[0].pageX, event.touches[0].pageY);

		} else {

			var x = 0.5 * (event.touches[0].pageX + event.touches[1].pageX);
			var y = 0.5 * (event.touches[0].pageY + event.touches[1].pageY);

			panStart.set(x, y);

		}

	}

	function handleTouchStartMove(event) {

		var dx = event.touches[0].pageX - event.touches[1].pageX;
		var dy = event.touches[0].pageY - event.touches[1].pageY;

		var distance = Math.sqrt(dx * dx + dy * dy);

		moveStart.set(0, distance);

	}

	function handleTouchStartMovePan(event) {

		if (scope.enableMove) handleTouchStartMove(event);

		if (scope.enablePan) handleTouchStartPan(event);

	}

	function handleTouchStartMoveRotate(event) {

		if (scope.enableMove) handleTouchStartMove(event);

		if (scope.enableRotate) handleTouchStartRotate(event);

	}

	function handleTouchMoveRotate(event) {

		if (event.touches.length == 1) {

			rotateEnd.set(event.touches[0].pageX, event.touches[0].pageY);

		} else {

			var x = 0.5 * (event.touches[0].pageX + event.touches[1].pageX);
			var y = 0.5 * (event.touches[0].pageY + event.touches[1].pageY);

			rotateEnd.set(x, y);

		}

		rotateDelta.subVectors(rotateEnd, rotateStart).multiplyScalar(scope.rotateSpeed);

		var element = scope.domElement;

		rotateLeft(2 * Math.PI * rotateDelta.x / element.clientHeight); // yes, height

		rotateUp(2 * Math.PI * rotateDelta.y / element.clientHeight);

		rotateStart.copy(rotateEnd);

	}

	function handleTouchMovePan(event) {

		if (event.touches.length == 1) {

			panEnd.set(event.touches[0].pageX, event.touches[0].pageY);

		} else {

			var x = 0.5 * (event.touches[0].pageX + event.touches[1].pageX);
			var y = 0.5 * (event.touches[0].pageY + event.touches[1].pageY);

			panEnd.set(x, y);

		}

		panDelta.subVectors(panEnd, panStart).multiplyScalar(scope.panSpeed);

		pan(panDelta.x, panDelta.y);

		panStart.copy(panEnd);

	}

	function handleTouchMoveMove(event) {

		var dx = event.touches[0].pageX - event.touches[1].pageX;
		var dy = event.touches[0].pageY - event.touches[1].pageY;

		var distance = Math.sqrt(dx * dx + dy * dy);

		moveEnd.set(0, distance);

		moveDelta.set(0, -1 * (moveEnd.y - moveStart.y) * scope.moveSpeed / 5);
		// moveDelta.set( 0, Math.pow( moveEnd.y / moveStart.y, scope.moveSpeed ) );

		moveOut(moveDelta.y);

		moveStart.copy(moveEnd);

	}

	function handleTouchMoveMovePan(event) {

		if (scope.enableMove) handleTouchMoveMove(event);

		if (scope.enablePan) handleTouchMovePan(event);

	}

	function handleTouchMoveMoveRotate(event) {

		if (scope.enableMove) handleTouchMoveMove(event);

		if (scope.enableRotate) handleTouchMoveRotate(event);

	}

	function handleTouchEnd( /*event*/) {

		// no-op

	}

	//
	// event handlers - FSM: listen for events and reset state
	//

	function onPointerDown(event) {

		if (scope.enabled === false) return;

		switch (event.pointerType) {

			case 'mouse':
			case 'pen':
				onMouseDown(event);
				break;

			// TODO touch

		}

	}

	function onPointerMove(event) {

		if (scope.enabled === false) return;

		switch (event.pointerType) {

			case 'mouse':
			case 'pen':
				onMouseMove(event);
				break;

			// TODO touch

		}

	}

	function onPointerUp(event) {

		switch (event.pointerType) {

			case 'mouse':
			case 'pen':
				onMouseUp(event);
				break;

			// TODO touch

		}

	}

	function onMouseDown(event) {

		// Prevent the browser from scrolling.
		event.preventDefault();

		// Manually set the focus since calling preventDefault above
		// prevents the browser from setting it automatically.

		scope.domElement.focus ? scope.domElement.focus() : window.focus();

		var mouseAction;

		switch (event.button) {

			case 0:

				mouseAction = scope.mouseButtons.LEFT;
				break;

			case 1:

				mouseAction = scope.mouseButtons.MIDDLE;
				break;

			case 2:

				mouseAction = scope.mouseButtons.RIGHT;
				break;

			default:

				mouseAction = -1;

		}

		switch (mouseAction) {

			case MOUSE.DOLLY:

				if (scope.enableMove === false) return;

				handleMouseDownMove(event);

				state = STATE.MOVE;

				break;

			case MOUSE.ROTATE:

				if (event.ctrlKey || event.metaKey || event.shiftKey) {

					if (scope.enablePan === false) return;

					handleMouseDownPan(event);

					state = STATE.PAN;

				} else {

					if (scope.enableRotate === false) return;

					handleMouseDownRotate(event);

					state = STATE.ROTATE;

				}

				break;

			case MOUSE.PAN:

				if (event.ctrlKey || event.metaKey || event.shiftKey) {

					if (scope.enableRotate === false) return;

					handleMouseDownRotate(event);

					state = STATE.ROTATE;

				} else {

					if (scope.enablePan === false) return;

					handleMouseDownPan(event);

					state = STATE.PAN;

				}

				break;

			default:

				state = STATE.NONE;

		}

		if (state !== STATE.NONE) {

			scope.domElement.ownerDocument.addEventListener('pointermove', onPointerMove);
			scope.domElement.ownerDocument.addEventListener('pointerup', onPointerUp);

			scope.dispatchEvent(startEvent);

		}

	}

	function onMouseMove(event) {

		if (scope.enabled === false) return;

		event.preventDefault();

		switch (state) {

			case STATE.ROTATE:

				if (scope.enableRotate === false) return;

				handleMouseMoveRotate(event);

				break;

			case STATE.MOVE:

				if (scope.enableMove === false) return;

				handleMouseMoveMove(event);

				break;

			case STATE.PAN:

				if (scope.enablePan === false) return;

				handleMouseMovePan(event);

				break;

		}

	}

	function onMouseUp(event) {

		scope.domElement.ownerDocument.removeEventListener('pointermove', onPointerMove);
		scope.domElement.ownerDocument.removeEventListener('pointerup', onPointerUp);

		if (scope.enabled === false) return;

		handleMouseUp(event);

		scope.dispatchEvent(endEvent);

		state = STATE.NONE;

	}

	function onMouseWheel(event) {

		if (scope.enabled === false || scope.enableMove === false || (state !== STATE.NONE && state !== STATE.ROTATE)) return;

		event.preventDefault();
		event.stopPropagation();

		scope.dispatchEvent(startEvent);

		handleMouseWheel(event);

		scope.dispatchEvent(endEvent);

	}

	function onKeyDown(event) {

		if (scope.enabled === false || scope.enablePan === false) return;

		handleKeyDown(event);

	}

	function onKeyUp(event) {

		if (scope.enabled === false || scope.enablePan === false) return;

		handleKeyUp(event);

	}

	function onTouchStart(event) {

		if (scope.enabled === false) return;

		// event.preventDefault(); // prevent scrolling

		switch (event.touches.length) {

			case 1:

				switch (scope.touches.ONE) {

					case TOUCH.ROTATE:

						if (scope.enableRotate === false) return;

						handleTouchStartRotate(event);

						state = STATE.TOUCH_ROTATE;

						break;

					case TOUCH.PAN:

						if (scope.enablePan === false) return;

						handleTouchStartPan(event);

						state = STATE.TOUCH_PAN;

						break;

					default:

						state = STATE.NONE;

				}

				break;

			case 2:

				switch (scope.touches.TWO) {

					case TOUCH.DOLLY_PAN:

						if (scope.enableMove === false && scope.enablePan === false) return;

						handleTouchStartMovePan(event);

						state = STATE.TOUCH_MOVE_PAN;

						break;

					case TOUCH.DOLLY_ROTATE:

						if (scope.enableMove === false && scope.enableRotate === false) return;

						handleTouchStartMoveRotate(event);

						state = STATE.TOUCH_MOVE_ROTATE;

						break;

					default:

						state = STATE.NONE;

				}

				break;

			default:

				state = STATE.NONE;

		}

		if (state !== STATE.NONE) {

			scope.dispatchEvent(startEvent);

		}

	}

	function onTouchMove(event) {

		if (scope.enabled === false) return;

		event.preventDefault(); // prevent scrolling
		event.stopPropagation();

		switch (state) {

			case STATE.TOUCH_ROTATE:

				if (scope.enableRotate === false) return;

				handleTouchMoveRotate(event);

				scope.update();

				break;

			case STATE.TOUCH_PAN:

				if (scope.enablePan === false) return;

				handleTouchMovePan(event);

				scope.update();

				break;

			case STATE.TOUCH_MOVE_PAN:

				if (scope.enableMove === false && scope.enablePan === false) return;

				handleTouchMoveMovePan(event);

				scope.update();

				break;

			case STATE.TOUCH_MOVE_ROTATE:

				if (scope.enableMove === false && scope.enableRotate === false) return;

				handleTouchMoveMoveRotate(event);

				scope.update();

				break;

			default:

				state = STATE.NONE;

		}

	}

	function onTouchEnd(event) {

		if (scope.enabled === false) return;

		handleTouchEnd(event);

		scope.dispatchEvent(endEvent);

		state = STATE.NONE;

	}

	function onContextMenu(event) {

		if (scope.enabled === false) return;

		event.preventDefault();

	}

	//

	scope.domElement.addEventListener('contextmenu', onContextMenu);

	scope.domElement.addEventListener('pointerdown', onPointerDown);
	scope.domElement.addEventListener('wheel', onMouseWheel);

	scope.domElement.addEventListener('touchstart', onTouchStart);
	scope.domElement.addEventListener('touchend', onTouchEnd);
	scope.domElement.addEventListener('touchmove', onTouchMove);

	// force an update at start

	this.update();

};

ItinerarisControls.prototype = Object.create(EventDispatcher.prototype);
ItinerarisControls.prototype.constructor = ItinerarisControls;

export default ItinerarisControls;
